import { Badge } from '../../ui/badge';
import { cn } from '../../lib/utils';
import { formatIsoDateTimeShort } from '../../utils/timeUtils';
import type { PriorDeliveryGuidanceEntryDto } from './types';

const GUIDANCE_CHIP_MAX = 72;

function truncateGuidance(text: string, max = GUIDANCE_CHIP_MAX): string {
    const trimmed = text.trim();
    if (trimmed.length <= max) return trimmed;
    return `${trimmed.slice(0, max - 1).trimEnd()}…`;
}

function sortGuidanceEntries(
    entries: PriorDeliveryGuidanceEntryDto[]
): PriorDeliveryGuidanceEntryDto[] {
    return [...entries].sort((a, b) => {
        const aTime = a.e251_CompletedAt ? Date.parse(a.e251_CompletedAt) : 0;
        const bTime = b.e251_CompletedAt ? Date.parse(b.e251_CompletedAt) : 0;
        return bTime - aTime;
    });
}

interface LessonCustomisationHistorySectionProps {
    entries?: PriorDeliveryGuidanceEntryDto[];
    className?: string;
}

/** Subtle audit trail of AI customisation guidance on a lesson card. */
export function LessonCustomisationHistorySection({
    entries,
    className
}: LessonCustomisationHistorySectionProps) {
    const visible = (entries ?? [])
        .map(entry => ({
            guidance: (entry.e251_Guidance ?? '').trim(),
            recordedAt: entry.e251_CompletedAt ?? null
        }))
        .filter(entry => entry.guidance.length > 0);

    if (visible.length === 0) return null;

    const sorted = sortGuidanceEntries(
        visible.map(v => ({
            e251_Guidance: v.guidance,
            e251_CompletedAt: v.recordedAt
        }))
    );

    return (
        <div
            className={cn('px-4 pb-3 border-t border-gray-100', className)}
            onClick={e => e.stopPropagation()}
        >
            <div className="mb-1.5 mt-3 text-xs font-medium text-gray-500">Customisation history</div>
            <div className="flex flex-wrap gap-1.5">
                {sorted.map((entry, index) => {
                    const guidance = (entry.e251_Guidance ?? '').trim();
                    const dateLabel = formatIsoDateTimeShort(entry.e251_CompletedAt ?? null);
                    const chipLabel = dateLabel
                        ? `${dateLabel} — ${truncateGuidance(guidance)}`
                        : truncateGuidance(guidance);

                    return (
                        <Badge
                            key={`${entry.e251_CompletedAt ?? 'undated'}-${index}`}
                            variant="outline"
                            title={guidance}
                            className="max-w-full rounded-md border-gray-300/90 bg-gray-50 px-2 py-0.5 text-xs font-normal leading-snug text-gray-600 shadow-none hover:bg-gray-100"
                        >
                            <span className="line-clamp-2 text-left">{chipLabel}</span>
                        </Badge>
                    );
                })}
            </div>
        </div>
    );
}
